import React, {useMemo} from "react";
import {Badge, Tabs} from "antd";
import {useNavigate, useParams} from "react-router-dom";
import LoadingSpinner from "../../../components/LoadingSpinner";
import InfoTooltip from "../../../components/InfoTooltip";
import {useProjectForm} from "../../../providers/ProjectFormProvider";
import PositionDeliverablesTable from "./PositionDeliverablesTable";
import Project from "../../../models/Project";
import ProjectPercentMarkupForm from "../project-scope/ProjectPercentMarkupForm";
import FixedFeeScopingForm from "../project-tools/FixedFeeScopingForm";
import PositionFormProvider from "../../../providers/PositionFormProvider";

const ManageProjectPositionsScopeTable = (props) => {

    const navigate = useNavigate();
    const {
        project,
        formModel,
        isLoading,
        isDisabled,
        onFormChange,
        isProjectUpdating,
        onFormReset,
        onFormSubmit,
    } = useProjectForm();

    const {roleId} = useParams();
    const totalHours = useMemo(() => {
        if (!formModel?.positions) return 0;

        return TB.sumBy(formModel?.positions, position => {

            let defaultTotal = TB.sumBy(TB.filter(position?.deliverables || [], ['is_active', true]), 'total_hours');
            if (position.assigned_candidate) {
                return position.assigned_candidate?.job_candidate?.total_hours || defaultTotal;
            }
            return defaultTotal;
        })
    }, [isProjectUpdating,isDisabled, isLoading,TB.flatMap(formModel?.positions, 'deliverables')]);

    const estimatedTalentCost = useMemo(() => {
        if (!formModel?.positions) return 0;


        return TB.sumBy(formModel?.positions, position => {
            // let defaultTotal = TB.sumBy(TB.filter(position?.deliverables || [], ['is_active', true]), 'total_hours');
            //
            // let defaultTalentCost = (position.exact_standard_platform_rate / (1 +(project.percent_markup || 0))) * defaultTotal;
            if (position.assigned_candidate) {
                return position.assigned_candidate.job_candidate?.exact_premarkup_cost || position.exact_premarkup_cost;
            }
            console.log('exact', position.exact_premarkup_cost)
            if (!position.exact_premarkup_cost && position.budget) {
                return position.budget / (1 +(project.percent_markup || 0));
            }
            return position.exact_premarkup_cost
        })
    }, [isProjectUpdating,isDisabled, isLoading,totalHours,TB.flatMap(formModel?.positions, 'exact_premarkup_cost')]);

    const estimatedPlatformCost = useMemo(() => {
        if (!formModel?.positions) return 0;

        return TB.sumBy(formModel?.positions, position => {
            if (position.assigned_candidate) {
                return position.assigned_candidate.job_candidate?.exact_platform_cost || position.exact_platform_cost;
            }
            if (!position.exact_platform_cost && position.budget) {
                return position.budget;
            }
            return position.exact_platform_cost
        })
    }, [isProjectUpdating,isDisabled, isLoading, TB.flatMap(formModel?.positions, 'exact_platform_cost')]);

    const currentPosition = useMemo(() => {
        if (!project?.positions?.length) {
            return null;
        }
        if (!roleId || !TB.find(project.positions, ['id', parseInt(roleId)])) {
            return TB.first(project.positions)
        }
        return TB.find(project.positions, ['id', parseInt(roleId)])
    }, [project, isLoading, isProjectUpdating, roleId]);

    const onChangePosition = (key) => {
        console.log('onChangePosition', key)
        return navigate(`../${key}`)
    }

    return (
        <div className="manage-scope-table flex flex-col items-stretch gap-3">
            {project && !isLoading && currentPosition && (
                <React.Fragment>
                    <div className="flex-grow flex flex-row justify-between bg-white gap-14 px-4 py-4">

                        {!project.hasConfirmedProjectScope() && (
                            <button disabled={isProjectUpdating || isDisabled}
                                    className="btn btn-dark self-center px-2 py-1.5 text-xs" onClick={() => {
                                let contract_type = TB.invert(laravel.project_contract_types)[project.isFixedFee() ? Project.ContractType.TimeMaterials : Project.ContractType.FixedFee];
                                onFormSubmit({contract_type: parseInt(contract_type)})
                            }}>
                                Switch To T&M
                            </button>
                        )}

                        <ProjectPercentMarkupForm/>

                        {totalHours > 0 && (
                            <div className="flex flex-col gap-1.5 items-start">
                                <p className="text-xs font-semibold">
                                    Total Hours
                                </p>
                                <p className="text-primary text-lg">
                                    {totalHours} hours
                                </p>
                            </div>
                        )}

                        <div className="flex flex-col gap-1.5 items-start">
                            <p className="text-xs font-semibold flex flex-row gap-1.5 items-center">
                                    <span>
                                        Est. Talent Payout
                                    </span>
                                <InfoTooltip placement="right" type="dark">
                                    <span className="text-xs px-2 py-3 text-white">
                                        Estimated Payout is based on the Standard Platform Market Rate for each
                                        role. If candidates have been selected, the proposed payout will be used instead.
                                    </span>
                                </InfoTooltip>
                            </p>
                            <p className="text-primary text-lg">
                                {TB.formatCurrency(estimatedTalentCost, false)}
                            </p>
                        </div>

                        <div className="flex flex-col gap-1.5 items-start">
                            <p className="text-xs font-semibold flex flex-row gap-1.5 items-center">
                                <span>
                                    Est. Client Cost
                                </span>
                                <InfoTooltip placement="right" type="dark">
                                    <span className="text-xs px-2 py-3 text-white">
                                        Estimated Cost is based on the Standard Platform Market Rate for each
                                        role. If candidates have been selected, the proposed cost will be used instead.
                                        The cost includes each position's markup fee.
                                    </span>
                                </InfoTooltip>
                            </p>
                            <p className="text-primary text-lg">
                                {TB.formatCurrency(estimatedPlatformCost, false)}
                            </p>
                        </div>
                        <FixedFeeScopingForm/>
                    </div>
                    <div className="bg-white px-4">
                        <div className="flex flex-col gap-4">

                            <Tabs
                                tabPosition="top"
                                onChange={onChangePosition}
                                activeKey={currentPosition?.id}
                                size="large"
                                items={
                                    TB.map(TB.orderBy(project.positions, 'id'), position => {
                                        const valid = position.hasValidFixedFeeDeliverables();
                                        return {
                                            label: (
                                                <Badge dot={!valid}
                                                       color={valid ? TB.config('color.secondary') : undefined}
                                                       count={!valid ? 1 : (position.total_interested_uninvited_candidates)}>
                                                <span
                                                    className={TB.mapClass("text-sm text-ellipsis overflow-hidden max-w-[250px]", {
                                                        "font-medium": currentPosition.id === position.id,
                                                        "mr-1.5": !valid,
                                                        "mr-3": valid && position.total_interested_uninvited_candidates > 0
                                                    })}>
                                                    {position.title}
                                                </span>
                                                </Badge>
                                            ),
                                            key: position.id,
                                            children: (
                                                <PositionFormProvider positionId={position.id}>
                                                    <PositionDeliverablesTable />
                                                </PositionFormProvider>
                                            ),
                                        }
                                    })
                                }

                            />
                        </div>
                    </div>
                </React.Fragment>
            )}

            {(!project || isProjectUpdating) && (
                <div
                    className="inset-0 absolute z-10 flex flex-col items-center justify-center bg-[rgba(0,0,0,0.05)]">
                    <LoadingSpinner type="primary" size="lg"/>
                </div>
            )}
        </div>
    )
}

export default ManageProjectPositionsScopeTable